/**
 * The live transcript, one reducer for both clients
 * (docs/plans/os-voice-conversation.md, C10 and rules 9 to 11): the
 * version-2 frames (`line`, `next`, `replaced`, `parked`) and the kept `turn`
 * fold into the rows the thread draws while live mode runs.
 *
 * Every row is keyed by the server's id. A line arrives whole each time under
 * its id and replaces its row; a kept thread message replaces the live row of
 * the same id in place, never appends beside it. The person's words are faint
 * while partial and solid once final (side talk never arrives). OS's lines
 * stream into their own message: a greeting is a plain OS message, and an
 * acknowledgement, the gist of an answer, a line that only fills a wait or
 * one with no words is heard but never drawn (J10 as revised by Devendra on
 * 7 and 12 Oct; filler-lines.ts).
 */
import type { LiveLineKind, LiveServerFrame, LiveVia } from '../agent-live-wire';
import { isThreadLine } from './filler-lines';

export type LiveYouRow = {
  readonly role: 'you';
  readonly id: string;
  readonly text: string;
  readonly partial: boolean;
  readonly via: LiveVia;
  /** When its last words were heard (spoken-order.ts): the server says it once the line is final. */
  readonly at?: number;
  /** The language it was said in, when the server says (its text is the app's language). */
  readonly spoken?: 'en' | 'hi' | 'hi-Latn';
};
export type LiveOsRow = {
  readonly role: 'os';
  readonly id: string;
  readonly text: string;
  readonly streaming: boolean;
  readonly kind: LiveLineKind;
  /** A gist's reply. */
  readonly replyTo?: string;
  /** When it opened (spoken-order.ts). */
  readonly at?: number;
};
/** A message the thread now keeps: the thread draws it, the transcript only holds its place. */
export type LiveKeptRow = { readonly role: 'kept'; readonly id: string; readonly messageRole: string };
export type LiveRow = LiveYouRow | LiveOsRow | LiveKeptRow;

export type LiveNext = { readonly id: string; readonly text: string; readonly via: LiveVia };

export type LiveTranscript = {
  readonly rows: readonly LiveRow[];
  /** The one visible Next, with Cancel. */
  readonly next: LiveNext | null;
  /** Requests a spoken stop parked. */
  readonly parked: readonly string[];
  /** Requests a correction replaced, each with the request that took its place. */
  readonly replaced: Readonly<Record<string, string>>;
};

export const NEW_LIVE_TRANSCRIPT: LiveTranscript = { rows: [], next: null, parked: [], replaced: {} };

/** A kept turn, as the transcript reads it: its messages' ids and roles. */
export type LiveTurnEvent = { readonly type: 'turn'; readonly messages: readonly { readonly id: string; readonly role: string }[] };

export type LiveTranscriptEvent = Extract<LiveServerFrame, { type: 'line' | 'next' | 'replaced' | 'parked' }> | LiveTurnEvent;

const upsert = (rows: readonly LiveRow[], row: LiveRow): readonly LiveRow[] => {
  const at = rows.findIndex((one) => one.id === row.id);
  if (at === -1) return [...rows, row];
  return rows.map((one, index) => (index === at ? row : one));
};

export const liveTranscriptStep = (transcript: LiveTranscript, event: LiveTranscriptEvent): LiveTranscript => {
  switch (event.type) {
    case 'line': {
      if (event.role === 'user') {
        const row: LiveYouRow = {
          role: 'you',
          id: event.id,
          text: event.text,
          partial: !event.final,
          via: event.via ?? 'voice',
          ...(event.at === undefined ? {} : { at: event.at }),
          ...(event.spoken === undefined ? {} : { spoken: event.spoken }),
        };
        const next = transcript.next?.id === event.id ? null : transcript.next;
        return { ...transcript, rows: upsert(transcript.rows, row), next };
      }
      const kind = event.kind ?? 'say';
      // An acknowledgement, a gist, a line that only fills a wait, or one with no words is heard and never drawn (J10 revised, 7 and 12 Oct).
      if (!isThreadLine(kind, event.text)) {
        return transcript.rows.some((row) => row.id === event.id) ? { ...transcript, rows: transcript.rows.filter((row) => row.id !== event.id) } : transcript;
      }
      const held = transcript.rows.find((row): row is LiveOsRow => row.role === 'os' && row.id === event.id);
      const row: LiveOsRow = {
        role: 'os',
        id: event.id,
        text: event.text,
        streaming: !event.final,
        kind,
        ...(event.replyTo === undefined ? {} : { replyTo: event.replyTo }),
        ...(event.at === undefined ? (held?.at === undefined ? {} : { at: held.at }) : { at: event.at }),
      };
      return { ...transcript, rows: upsert(transcript.rows, row) };
    }
    case 'next':
      return { ...transcript, next: event.id === null ? null : { id: event.id, text: event.text, via: event.via } };
    case 'replaced':
      return { ...transcript, replaced: { ...transcript.replaced, [event.messageId]: event.by } };
    case 'parked':
      return transcript.parked.includes(event.messageId) ? transcript : { ...transcript, parked: [...transcript.parked, event.messageId] };
    case 'turn': {
      let rows = transcript.rows;
      for (const message of event.messages) rows = upsert(rows, { role: 'kept', id: message.id, messageRole: message.role });
      const kept = new Set(event.messages.map((message) => message.id));
      return {
        ...transcript,
        rows,
        parked: transcript.parked.filter((id) => !kept.has(id)),
        next: transcript.next !== null && kept.has(transcript.next.id) ? null : transcript.next,
      };
    }
  }
};

/** Folds a run of events from a fresh transcript. */
export const liveTranscriptOf = (events: readonly LiveTranscriptEvent[], from: LiveTranscript = NEW_LIVE_TRANSCRIPT): LiveTranscript =>
  events.reduce(liveTranscriptStep, from);
